/**
 * Camera schedule row — one capture-schedule entry in CameraScheduleList.
 *
 * Tap-to-edit row showing the capture time, active days (the same day-pill
 * row used by relay schedules), and a muted "Disabled" marker for inactive
 * schedules. Opens the existing CameraScheduleDialog on click.
 *
 * Contains:
 * - CameraScheduleListItem — the schedule row (default export)
 */
import { Box, ListItemButton, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CameraScheduleDialog from '@sophocles/views/sites/components/CameraCards/CameraScheduleDialog';
import { buildDayPillBooleans, DayPills } from '@sophocles/views/sites/components/RelayCards/dayPills';

/**
 * @param {object}        props
 * @param {object}        props.schedule - plain-JS camera schedule ({ id, time, days, enabled })
 * @param {object}        props.camera   - parent camera, forwarded to the edit dialog
 * @param {number|string} props.siteId   - required for the schedule write endpoints
 */
function CameraScheduleListItem({ schedule, camera, siteId }) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  const displayTime = moment(schedule.time, 'HH:mm:ss').format('h:mm A');
  const enabled = schedule.enabled ?? true;
  const dayPillBooleans = buildDayPillBooleans(schedule.days ?? 0);

  return (
    <>
      <ListItemButton divider onClick={() => setDialogOpen(true)} sx={{ display: 'block', py: 1, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, opacity: enabled ? 1 : 0.6 }}>
            {displayTime}
          </Typography>
          {!enabled ? (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('general.disabled', 'Disabled')}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ opacity: enabled ? 1 : 0.6 }}>
          <DayPills days={dayPillBooleans} />
        </Box>
      </ListItemButton>
      <CameraScheduleDialog
        siteId={siteId}
        camera={camera}
        schedule={schedule}
        open={dialogOpen}
        closeDialog={() => setDialogOpen(false)}
      />
    </>
  );
}

export default CameraScheduleListItem;
